import {Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {BehaviorSubject, catchError, map, Observable, of, tap} from "rxjs";
import {CookieService} from "ngx-cookie-service";
import {Tokens} from "../model/tokens";
import {constants} from "../constants";
import {Router} from "@angular/router";
import {MetrikaService} from "./metrika.service";
import {Subscriber} from "../model/subscriber";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private tokens: Tokens;
  private _authenticated: boolean;
  private readonly _authStateChanged: BehaviorSubject<boolean>;
  private subscribed: boolean;

  constructor(private readonly httpClient: HttpClient,
              private readonly cookieService: CookieService,
              private readonly router: Router,
              private readonly metrikaService: MetrikaService) {
    this.tokens = null;
    this._authenticated = false;
    this._authStateChanged = new BehaviorSubject<boolean>(false);
    this.subscribed = true;
  }

  get authenticated(): boolean {
    return this._authenticated;
  }

  get authStateChanged(): Observable<boolean> {
    return this._authStateChanged.asObservable();
  }

  get accessToken(): string {
    return this.tokens?.accessToken;
  }

  get refreshToken(): string {
    return this.tokens?.refreshToken;
  }

  get msisdn(): string {
    return this.tokens?.msisdn;
  }

  login(msisdn: string, password: string): Observable<number> {
    return this.httpClient
      .post<Tokens>('/auth/login', {msisdn: msisdn, password: password}, {
        observe: 'response',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}
      })
      .pipe(
        map(response => {
          const status = response.status;
          if (status >= 200 && status < 300) {
            this.metrikaService.encryptMsisdn(msisdn);
            this.metrikaService.addEvent('authorization_successful', msisdn, 'SUCCESS')
            this.loginSuccess(response.body);
          }
          return status;
        }),
        catchError((error: HttpErrorResponse) => of(error.status))
      );
  }

  sendPassword(msisdn: string): Observable<number> {
    return this.httpClient
      .post<void>('/auth/password', {msisdn: msisdn}, {
        observe: 'response',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}
      })
      .pipe(
        map(response => response.status),
        catchError((error: HttpErrorResponse) => of(error.status))
      );
  }

  refreshTokens(token: string): Observable<Tokens> {
    return this.httpClient.post<Tokens>('/auth/refresh', null, {
      headers: {'Authorization': token}
    });
  }

  validateTokens(): void {
    this.httpClient.get<Tokens>('/auth/check', {
      headers: {
        Authorization: this.accessToken
      }
    })
      .pipe(
        catchError(() => of(null))
      ).subscribe(tokens => {
      if (tokens) {
        this.loginSuccess(tokens);
      } else {
        this.logout();
      }
    });
  }

  loadTokens(): void {
    if (this.cookieService.check(constants.accessTokenCookieName) && this.cookieService.check(constants.refreshTokenCookieName)) {
      this.tokens = new Tokens(this.cookieService.get(constants.accessTokenCookieName),
        this.cookieService.get(constants.refreshTokenCookieName))
      this._authenticated = true;
      this.validateTokens();
    } else if (this.cookieService.check(constants.accessTokenCookieName) || this.cookieService.check(constants.refreshTokenCookieName)) {
      this.logout();
    }
  }

  saveTokens(tokens: Tokens): void {
    this.tokens = tokens;
    this.cookieService.set(constants.accessTokenCookieName, tokens.accessToken, 30, '/');
    this.cookieService.set(constants.refreshTokenCookieName, tokens.refreshToken, 30, '/');
  }

  private clearTokens(): void {
    this.tokens = null;
    this.cookieService.delete(constants.accessTokenCookieName, '/');
    this.cookieService.delete(constants.refreshTokenCookieName, '/');
    this.cookieService.delete(constants.encryptedMsisdn, '/');
  }

  private loginSuccess(tokens: Tokens): void {
    this.saveTokens(tokens);
    this._authenticated = true;
    this._authStateChanged.next(true);
  }

  private activatePrivetPlus(msisdn: string): void {
    this.httpClient.get<Subscriber>('/rbt/subscriptions')
      .pipe(catchError(() => {
        return of(null)
      })).subscribe(subscriber => {
      if (!subscriber.activeSubscription) {
        this.activateSubscriptions(true, msisdn)
      }
    });
  }

  private activateSubscriptions(privetPlus: boolean, msisdn: string): void {
    this.httpClient.put<string>('/rbt/subscriptions/' + (privetPlus ? 'privet-plus' : 'privet'), null)
      .pipe(tap(response => {
          if (response == 'SUCCESS') {
            this.metrikaService.addEvent(privetPlus ? 'activate_privet_plus' : 'activate_privet', msisdn, response)
          } else {
            this.metrikaService.addEvent(privetPlus ? 'activate_privet_plus_failed' : 'activate_privet_failed', msisdn, response)
          }
        }
      )).subscribe();
  }

  logout(): void {
    this.clearTokens();
    this._authenticated = false;
    this._authStateChanged.next(false);

    if (this.router.url.startsWith('/subscriber')) {
      this.router.navigate(['']);
    }
  }
}
